import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { getTeachersBySchoolId, updateTeacherProfileWithEmail, getRoles, getSchools } from '../services/APIClient';

const EditTeacherModal = ({ show, onHide, schoolid }) => {
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [teacherData, setTeacherData] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    additionalRoles: []
  });
  const [roleDefs, setRoleDefs] = useState([]);
  const [countryId, setCountryId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Fetch roles and school data when modal opens
  useEffect(() => {
    if (show && schoolid) {
      loadData();
      loadRolesAndCountry();
    }
  }, [show, schoolid]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      resetForm();
    }
  }, [show]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getTeachersBySchoolId(schoolid);
      console.log('Raw teachers response:', response);
      
      // The data structure looks perfect - use it directly
      const transformedTeachers = (response || []).map(teacher => ({
        id: teacher.id, // Keep as string (UUID)
        name: teacher.name || 'Unknown',
        email: teacher.email || '',
        mobileNumber: teacher.mobileNumber || '',
        additionalRoles: teacher.additionalRoles || [],
        active: teacher.active !== false
      })).filter(teacher => teacher.active) // Only show active teachers
        .sort((a, b) => a.name.localeCompare(b.name));
      
      console.log('Transformed teachers:', transformedTeachers);
      setTeachers(transformedTeachers);
    } catch (err) {
      setError('Failed to fetch teachers. Please try again.');
      console.error('Error fetching teachers:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRolesAndCountry = async () => {
    try {
      // Fetch roles
      const rolesResponse = await getRoles();
      const sortedRoles = rolesResponse.data.sort((a, b) => a.name.localeCompare(b.name));
      setRoleDefs(sortedRoles);

      // Fetch school info to get country ID
      const schoolsResponse = await getSchools();
      const matchingSchool = schoolsResponse.data.find(s => s.id === schoolid);
      if (matchingSchool) {
        setCountryId(matchingSchool.countryid);
      }
    } catch (err) {
      console.error('Error fetching roles or school data:', err);
    }
  };

  const resetForm = () => {
    setSelectedTeacher('');
    setTeacherData({ 
      name: '', 
      email: '', 
      mobileNumber: '', 
      additionalRoles: [] 
    });
    setError('');
    setSuccess('');
    setShowConfirmation(false);
  };

  const handleTeacherSelect = (teacherId) => {
    console.log('=== TEACHER SELECTION DEBUG ===');
    console.log('Selected teacher ID:', teacherId);
    console.log('Selected teacher ID type:', typeof teacherId);
    
    setSelectedTeacher(teacherId);
    // Fix: Don't parse as integer since IDs are UUIDs (strings)
    const teacher = teachers.find(t => t.id === teacherId);
    console.log('Found teacher object:', teacher);
    
    if (teacher) {
      console.log('Teacher properties:');
      console.log('- name:', teacher.name);
      console.log('- email:', teacher.email);
      console.log('- mobileNumber:', teacher.mobileNumber);
      console.log('- additionalRoles:', teacher.additionalRoles);
      
      // Auto-fill all existing data
      const newTeacherData = {
        name: teacher.name || '',
        email: teacher.email || '',
        mobileNumber: teacher.mobileNumber || '',
        additionalRoles: teacher.additionalRoles || []
      };
      
      console.log('Setting teacherData to:', newTeacherData);
      setTeacherData(newTeacherData);
    } else {
      console.log('No teacher found with ID:', teacherId);
      setTeacherData({ 
        name: '', 
        email: '', 
        mobileNumber: '', 
        additionalRoles: [] 
      });
    }
    setError('');
    setSuccess('');
    setShowConfirmation(false);
  };

  const handleInputChange = (field, value) => {
    setTeacherData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoleToggle = (role) => {
    setTeacherData(prev => {
      const currentRoles = prev.additionalRoles || [];
      const newRoles = currentRoles.includes(role)
        ? currentRoles.filter(r => r !== role)
        : [...currentRoles, role];
      
      return {
        ...prev,
        additionalRoles: newRoles
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedTeacher) {
      setError('Please select a teacher to update.');
      return;
    }

    if (!teacherData.name.trim() || !teacherData.email.trim()) {
      setError('Please fill in all required fields (Name and Email).');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(teacherData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Show confirmation dialog
    setShowConfirmation(true);
    setError('');
  };

  const handleConfirmUpdate = async () => {
    setSubmitting(true);
    setShowConfirmation(false);
    setError('');
    setSuccess('');

    try {
      // Use the new API function with all fields
      await updateTeacherProfileWithEmail(schoolid, selectedTeacher, {
        name: teacherData.name.trim(),
        email: teacherData.email.trim().toLowerCase(),
        mobileNumber: teacherData.mobileNumber.trim(),
        additionalRoles: teacherData.additionalRoles
      });
      
      setSuccess('Teacher information updated successfully!');
      
      // Refresh the teachers list to show updated data
      await loadData();
      
      // Reset selection but keep the modal open to show success message
      setTimeout(() => {
        resetForm();
      }, 2000);
      
    } catch (err) {
      const apiMessage = err.response?.data?.error?.message || err.response?.data?.message;
      setError(apiMessage || 'Failed to update teacher information. Please try again.');
      console.error('Error updating teacher:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="edit-teacher-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="edit-teacher-title">Edit/Update Teacher</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading teachers...</span>
            </Spinner>
            <p className="mt-2">Loading teachers...</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="edit-teacher-select">
              <Form.Label>Select Teacher *</Form.Label>
              <Form.Select
                value={selectedTeacher}
                onChange={(e) => handleTeacherSelect(e.target.value)}
                required
              >
                <option value="">-- Select a teacher --</option>
                {teachers.map(teacher => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.name} ({teacher.email})
                  </option>
                ))}
              </Form.Select>
              {teachers.length === 0 && !loading && (
                <Form.Text className="text-muted">
                  No active teachers found for this school.
                </Form.Text>
              )}
            </Form.Group>

            {selectedTeacher && (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="edit-teacher-name">
                      <Form.Label>Teacher Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={teacherData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter teacher's full name"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="edit-teacher-email">
                      <Form.Label>Email Address *</Form.Label>
                      <Form.Control
                        type="email"
                        value={teacherData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter teacher's email address"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3" controlId="edit-teacher-mobile">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="tel"
                    value={teacherData.mobileNumber}
                    onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                    placeholder="Enter teacher's mobile number (optional)"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Additional Roles</Form.Label>
                  <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {roleDefs
                      .filter(role => role.countryid === countryId)
                      .map(role => (
                        <Form.Check
                          key={role.name}
                          type="checkbox"
                          id={`role-${role.name}`}
                          label={role.name}
                          checked={teacherData.additionalRoles.includes(role.name)}
                          onChange={() => handleRoleToggle(role.name)}
                          className="mb-2"
                        />
                      ))}
                    {roleDefs.filter(role => role.countryid === countryId).length === 0 && (
                      <div className="text-muted">No roles available for this country</div>
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    Select any additional roles this teacher has (optional)
                  </Form.Text>
                </Form.Group>

                {teacherData.additionalRoles.length > 0 && (
                  <div className="mb-3">
                    <small className="text-muted">Selected roles: </small>
                    {teacherData.additionalRoles.map((role, index) => (
                      <span key={role} className="badge bg-secondary me-1">
                        {role}
                        {index < teacherData.additionalRoles.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </Form>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Close
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={!selectedTeacher || submitting || loading}
        >
          Update Teacher
        </Button>
      </Modal.Footer>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmation}
        onHide={handleCancelConfirmation}
        centered
        aria-labelledby="confirm-teacher-update-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="confirm-teacher-update-title">
            Confirm Teacher Update
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <h6>Are you sure you want to update the following teacher information?</h6>
          </div>
          
          <div className="border rounded p-3 bg-light">
            <div className="row mb-2">
              <div className="col-4"><strong>Teacher:</strong></div>
              <div className="col-8">{teachers.find(t => t.id === selectedTeacher)?.name}</div>
            </div>
            <div className="row mb-2">
              <div className="col-4"><strong>Name:</strong></div>
              <div className="col-8">{teacherData.name}</div>
            </div>
            <div className="row mb-2">
              <div className="col-4"><strong>Email:</strong></div>
              <div className="col-8">{teacherData.email}</div>
            </div>
            <div className="row mb-2">
              <div className="col-4"><strong>Mobile:</strong></div>
              <div className="col-8">{teacherData.mobileNumber || 'Not provided'}</div>
            </div>
            <div className="row">
              <div className="col-4"><strong>Roles:</strong></div>
              <div className="col-8">
                {teacherData.additionalRoles.length > 0 ? (
                  <div>
                    {teacherData.additionalRoles.map(role => (
                      <span key={role} className="badge bg-secondary me-1 mb-1">
                        {role}
                      </span>
                    ))}
                  </div>
                ) : (
                  'No additional roles'
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelConfirmation}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleConfirmUpdate}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              'Confirm Update'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default EditTeacherModal;
